import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { mockJudicialRequirements } from "@/mocks/judicialRequirements";

export default function JudicialInformesPage() {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleDownloadFile = (fileName: string, fileContent: string = "Simulated document content.") => {
    const element = document.createElement("a");
    const file = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
    element.href = URL.createObjectURL(file);
    element.download = fileName;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const enviados = mockJudicialRequirements.filter((r) => r.estado === "enviado_tribunal" || r.estado === "cerrado");
  const enProceso = mockJudicialRequirements.filter((r) =>
    ["borrador_generado", "en_revision", "aprobado", "informe_en_elaboracion"].includes(r.estado)
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">Informes Generados</h1>
          <p className="text-sm text-foreground-500 mt-1">Informes institucionales enviados y en preparación para tribunales</p>
        </div>
        <button onClick={() => navigate("/judicial")} className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-soft">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></span>
          Nuevo Requerimiento
        </button>
      </div>

      {/* En proceso */}
      <div>
        <h2 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          En preparación ({enProceso.length})
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {enProceso.map((req) => (
            <button
              key={req.id}
              onClick={() => navigate(`/judicial/requerimiento/${req.id}`)}
              className="text-left bg-white border border-background-200/70 rounded-xl p-4 hover:border-amber-200 hover:bg-amber-50/30 transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-foreground-500">RIT {req.rit}</span>
                <span className="text-xs text-foreground-300">·</span>
                <span className="text-xs text-foreground-600 font-medium">{req.tribunal}</span>
              </div>
              <p className="text-sm font-semibold text-foreground-800 mb-1">{req.tipoRequerimiento}</p>
              <div className="flex items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1"><i className="ri-user-line" />{req.studentName || "Institucional"}</span>
                <span className="flex items-center gap-1"><i className="ri-time-line" />Límite: {req.fechaLimite}</span>
              </div>
            </button>
          ))}
          {enProceso.length === 0 && (
            <p className="col-span-2 text-sm text-foreground-400 text-center py-8">No hay informes en preparación</p>
          )}
        </div>
      </div>

      {/* Enviados */}
      <div>
        <h2 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          Enviados a tribunal ({enviados.length})
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {enviados.map((req) => (
            <div key={req.id} className="bg-white border border-background-200/70 rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}
                className="w-full text-left p-4 hover:bg-background-50/50 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-foreground-500">RIT {req.rit}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-[11px] font-medium text-emerald-600">Enviado</span>
                  </div>
                  <span className="text-[11px] text-foreground-400">{req.fechaEnvio}</span>
                </div>
                <p className="text-sm font-semibold text-foreground-800 mb-1">{req.tribunal}</p>
                <p className="text-xs text-foreground-600 mb-2">{req.tipoRequerimiento}</p>
                <div className="flex items-center gap-3 text-xs text-foreground-500">
                  <span className="flex items-center gap-1"><i className="ri-user-line" />{req.studentName || "Institucional"}</span>
                  {req.comprobanteEnvioNombre && (
                    <span className="flex items-center gap-1 text-emerald-600"><i className="ri-check-double-line" />Comprobante adjunto</span>
                  )}
                </div>
              </button>

              {/* Expanded content */}
              {expandedId === req.id && (
                <div className="border-t border-background-200/50 p-4 bg-background-50/50">
                  <div className="space-y-1 text-xs">
                    <p className="text-foreground-500"><strong className="text-foreground-700">Documento enviado:</strong> {req.comprobanteEnvioNombre || "No especificado"}</p>
                    <p className="text-foreground-500"><strong className="text-foreground-700">Fecha de envío:</strong> {req.fechaEnvio}</p>
                    <p className="text-foreground-500"><strong className="text-foreground-700">Responsable:</strong> {req.responsableName}</p>
                    {req.observaciones && (
                      <div className="mt-2 pt-2 border-t border-background-200/50">
                        <p className="text-foreground-500 italic">{req.observaciones}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button 
                      onClick={() => handleDownloadFile(`Informe_Tribunal_${req.rit}.txt`, `Informe enviado para la causa RIT ${req.rit}.\nEstudiante: ${req.studentName || "Institucional"}\nTribunal: ${req.tribunal}`)}
                      className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
                    >
                      <i className="ri-file-download-line" /> Descargar informe
                    </button>
                    <button 
                      onClick={() => handleDownloadFile(req.comprobanteEnvioNombre || `Comprobante_Envio_${req.rit}.pdf`, `Comprobante de recepcion de informe para la causa RIT ${req.rit}.\nFecha de envio: ${req.fechaEnvio}`)}
                      className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
                    >
                      <i className="ri-file-copy-line" /> Descargar comprobante
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {enviados.length === 0 && (
            <p className="col-span-2 text-sm text-foreground-400 text-center py-8">No se han enviado informes aún</p>
          )}
        </div>
      </div>
    </div>
  );
}